/**
 * File: CashFlowChart.tsx
 * Path: apps/admin/src/shared/components/overview/CashFlowChart.tsx
 * Description: Money in against money out, by month or week, drawn to fill
 * whatever box it sits in. The scale fits the figures (see niceAxis), the
 * lines are plain SVG stretched to the box, and every column is a button so
 * the numbers are reachable by hover, tap and keyboard.
 */

import React from "react";
import { formatCents } from "@idk/config";
import { cn } from "@idk/utils";
import type { FlowPoint } from "../../utils/overviewFiguresUtils";
import { useCashFlowChartController } from "../../hooks/useCashFlowChartController";

/** "R 20k" for axis labels. */
const compact = (cents: number): string => {
  const rand = Math.round(cents / 100);
  if (rand >= 1_000_000) return `R ${rand / 1_000_000}m`;
  if (rand >= 1_000) return `R ${rand / 1_000}k`;
  return `R ${rand}`;
};

/** What the two lines are called: money in and out on the Overview; all money in and the care plan share on Today. */
export interface FlowNames {
  /** Tooltip and screen-reader words. */
  in: string;
  out: string;
  /** Legend words. */
  legendIn: string;
  legendOut: string;
}

const MONEY_NAMES: FlowNames = { in: "In", out: "Out", legendIn: "Money in", legendOut: "Money out" };

export const CashFlowChart: React.FC<{ points: readonly FlowPoint[]; className?: string; names?: FlowNames }> = ({
  points,
  className,
  names = MONEY_NAMES,
}) => {
  const { box, axis, count, x, y, line, active, shown, labelEvery, follow, onMouseMove, onMouseLeave, columnHandlers } =
    useCashFlowChartController(points);
  const GAP = "0.75rem";

  return (
    <div className={cn("flex h-full min-h-72 flex-col gap-stack-sm", className)}>
      <div className="flex min-h-0 flex-1 gap-stack-xs">
        <div className="relative w-14 shrink-0" aria-hidden>
          {axis.steps.map((step) => (
            <span
              key={step}
              className="absolute right-0 -translate-y-1/2 text-xs tabular-nums text-muted-foreground"
              style={{ top: `${y(step)}%` }}
            >
              {compact(step)}
            </span>
          ))}
        </div>
        <div
          ref={box}
          className="relative min-w-0 flex-1"
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
        >
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full overflow-visible"
            aria-hidden
          >
            {axis.steps.map((step) => (
              <line
                key={step}
                x1="0"
                x2="100"
                y1={y(step)}
                y2={y(step)}
                className="stroke-border"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {points.length > 0 && (
              <>
                <polygon points={`${x(0)},100 ${line((p) => p.inCents)} ${x(points.length - 1)},100`} className="fill-primary/15" />
                <polyline
                  points={line((p) => p.outCents)}
                  fill="none"
                  className="stroke-muted-foreground"
                  strokeWidth="2"
                  strokeDasharray="6 5"
                  vectorEffect="non-scaling-stroke"
                />
                <polyline
                  points={line((p) => p.inCents)}
                  fill="none"
                  className="stroke-primary"
                  strokeWidth="3"
                  vectorEffect="non-scaling-stroke"
                />
              </>
            )}
            {shown && (
              <line
                x1={x(active)}
                x2={x(active)}
                y1="0"
                y2="100"
                className="stroke-primary"
                strokeWidth="1"
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
          {points.map((p, i) => (
            <button
              key={p.start}
              type="button"
              aria-label={`${p.label}: ${formatCents(p.inCents)} ${names.in.toLowerCase()}, ${formatCents(p.outCents)} ${names.out.toLowerCase()}`}
              {...columnHandlers(i)}
              className="absolute inset-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              style={{ left: `${(i / count) * 100}%`, width: `${100 / count}%` }}
            />
          ))}
          {shown && (
            <div
              role="status"
              className="pointer-events-none absolute z-10 w-44 border border-border bg-card p-stack-sm text-xs shadow-md"
              // Follows the pointer, and flips to the other side of it near the right or bottom edge so it stays inside the box.
              style={{
                left: `${follow.fx * 100}%`,
                top: `${follow.fy * 100}%`,
                transform: `translate(${follow.fx > 0.6 ? `calc(-100% - ${GAP})` : GAP}, ${follow.fy > 0.6 ? `calc(-100% - ${GAP})` : GAP})`,
              }}
            >
              <p className="font-bold text-foreground">{shown.label}</p>
              <p className="text-foreground">{names.in} {formatCents(shown.inCents)}</p>
              <p className="text-muted-foreground">{names.out} {formatCents(shown.outCents)}</p>
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-stack-xs" aria-hidden>
        <span className="w-14 shrink-0" />
        <div className="flex min-w-0 flex-1">
          {points.map((p, i) => (
            <span
              key={p.start}
              className={cn("min-w-0 flex-1 text-center text-xs text-muted-foreground", i % labelEvery !== 0 && "invisible")}
            >
              {p.label}
            </span>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-gutter-md gap-y-stack-xs text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-stack-xs">
          <span className="h-0.5 w-5 bg-primary" aria-hidden /> {names.legendIn}
        </span>
        <span className="inline-flex items-center gap-stack-xs">
          <span className="h-0 w-5 border-t-2 border-dashed border-muted-foreground" aria-hidden /> {names.legendOut}
        </span>
      </div>
    </div>
  );
};
